import { type Registry as Services, service } from '@ember/service';
import Model, { attr, belongsTo } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type { DayjsTransformReturn } from 'frontend-utils/transforms/dayjs';
import type { CompareShortTermRentalOccupancy, Day } from 'lumen/models';

export default class ShortTermRentalOccupancy extends Model {
  [Type] = 'short-term-rental-occupancy' as const;

  @belongsTo('day', { async: false, inverse: 'rentalOccupancies' })
  declare day: Day;

  @attr('dayjs') declare extractDate: NonNullable<DayjsTransformReturn>;
  @attr('string') declare message: string;
  @attr('number') declare value: number;

  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];

  modelName = 'short-term-rental-occupancy';

  // Don't show changes smaller than 0.5% (will be rounded to 0)
  SIGNIFICANT_DIFFERENCE = 0.005;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedExtractDate() {
    return this.extractDate.format('YYYY-MM-DD');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedExtractDateLastYear() {
    return this.extractDate.subtract(52, 'weeks').format('YYYY-MM-DD');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedValue() {
    if (this.isMissing) {
      return this.intl.t('general.missing');
    }
    return this.intl.formatNumber(this.value, { style: 'percent' });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMissing() {
    return this.message === 'general.missing';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  compareObjectForDay(referenceChangeDays: number) {
    const referenceExtractDate = this.hotelAndMarketService
      .getLocalTime()
      .subtract(referenceChangeDays, 'day')
      .format('YYYY-MM-DD');
    return this.compareObjectForExtractDate(referenceExtractDate);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  compareObjectForExtractDate(referenceExtractDate: string) {
    return this.day.compareShortTermRentalOccupancies?.find(
      (item) => item.extractDate.format('YYYY-MM-DD') === referenceExtractDate,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  differenceForCompareObject(compareObject?: CompareShortTermRentalOccupancy) {
    // CompareObject is undefined when switching between compare period (e.g. vs 7 days ago -> vs. 14 days ago)
    if (
      this.isMissing ||
      compareObject === undefined ||
      compareObject.isMissing
    ) {
      return 0;
    }

    return this.value - compareObject.value;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  differenceForDay(referenceChangeDays: number) {
    const compareObject = this.compareObjectForDay(referenceChangeDays);
    return this.differenceForCompareObject(compareObject);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  differenceForExtractDate(referenceExtractDate: string) {
    const compareObject =
      this.compareObjectForExtractDate(referenceExtractDate);
    return this.differenceForCompareObject(compareObject);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  formattedDifferenceForCompareObject(
    compareObject?: CompareShortTermRentalOccupancy,
  ) {
    const difference = this.differenceForCompareObject(compareObject);
    if (
      this.isMissing ||
      Math.abs(difference) < this.SIGNIFICANT_DIFFERENCE ||
      compareObject === undefined
    ) {
      return '';
    }

    if (compareObject.isMissing) {
      return this.intl.t('general.missing');
    }

    return this.intl.formatNumber(difference, {
      style: 'percent',
      signDisplay: 'exceptZero',
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  formattedDifferenceForDay(referenceChangeDays: number) {
    const compareObject = this.compareObjectForDay(referenceChangeDays);
    return this.formattedDifferenceForCompareObject(compareObject);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  formattedDifferenceForExtractDate(referenceExtractDate: string) {
    const compareObject =
      this.compareObjectForExtractDate(referenceExtractDate);
    return this.formattedDifferenceForCompareObject(compareObject);
  }
}
